// Payment/discount/tip/invoice actions for a viewed order, extracted
// verbatim from ModalPosViewOrderPaymentStatus.vue so the compact money
// strip (PosV2ViewMoneyStrip) can trigger the same flows. The bodies form
// one closure group (doPayment's paid callback calls createInvoice) and
// must not be split.
import { computed, nextTick, ref, Ref } from 'vue';
import { OrderStatus } from '@/enums/OrderEnums';
import { DiscountType } from '@/enums/DiscountType';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { PaymentType } from '@/enums/PaymentEnums';
import { AlertType } from '@/interfaces/IAlert';
import { IDiscount } from '@/interfaces/IDiscount';
import { IOrder } from '@/interfaces/IOrder';
import { PrinterType } from '@/modules/PrintService';
import {
  isAutoInvoicePaymentMethod,
  orderHasAutoInvoicePayments,
  orderInvoiceableTotal,
} from '@/utils/orders.utils';
import { roundPrice } from '@/utils';
import { i18n } from '@/plugins/i18n';
import { isClubAttachAvailable } from '@/domain/club/club-member-panel';
import { clubProgramCoversBranch } from '@/domain/club/club-program-math';
import { useDiscounts } from '@/composition/use-discounts';
import { useInvoiceAttempt } from '@/composition/use-invoice-attempt';
import { useModal } from '@/composition/use-modal';
import { useOrders } from '@/composition/use-orders';
import { usePosMoney } from '@/composition/use-pos-money';
import { usePrinting } from '@/composition/use-printing';
import { useUi } from '@/composition/use-ui';
import { IOrderClubContext } from '@/composition/use-order-club-context';
import { useRestaurantStore } from '@/stores/restaurant.store';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import CreateInvoiceModalArg from '@/components/modals/order/CreateInvoiceModalArg.vue';
import ModalOrderDiscount from '@/components/modals/order/ModalOrderDiscount.vue';
import ModalPay, {
  IModalPayPresetProps,
} from '@/components/modals/pay/ModalPay.vue';
import V2ModalClubAttach from '@/components/modals/club/V2ModalClubAttach.vue';
import V2ModalClubRedeem from '@/components/modals/club/V2ModalClubRedeem.vue';
import { IClubRedeemResult } from '@/api/services/club.service';
import { IClubOrderMemberContext } from '@/interfaces/IClubMember';

/**
 * @param clubContext Shared member/grant context of the viewed order (POS V2
 * hosts). When present, the club flows skip the code step while the
 * cashier's identification grant is live.
 */
export function usePosOrderPayments(
  order: Ref<IOrder>,
  clubContext: IOrderClubContext | null = null
) {
  const restaurantStore = useRestaurantStore();
  const modals = useModal();
  const ui = useUi();
  const printing = usePrinting();
  const ordersHelper = useOrders();
  const discountsHelper = useDiscounts();
  const invoiceAttempt = useInvoiceAttempt();
  const money = usePosMoney();

  const isPendingOrder = computed(() => {
    const s = order.value?.status;

    return (
      s !== OrderStatus.Canceled &&
      s !== OrderStatus.Declined &&
      s !== OrderStatus.Received
    );
  });

  /**
   * Attached member + grant state for ModalPay: picking Saldo Club there
   * skips the code step while the grant is live, whichever button opened it.
   */
  function clubPresets(): IModalPayPresetProps {
    return {
      presetClubMember: clubContext?.member.value ?? null,
      presetClubVerified: clubContext?.verified.value ?? false,
    };
  }

  async function doPayment() {
    openPayModal(clubPresets());
  }

  /** "Usar saldo": ModalPay with Saldo Club and the member preselected. */
  async function payWithClubBalance() {
    // Latest grant state right before opening (refresh never throws).
    await clubContext?.refresh();
    openPayModal({ presetMethod: PaymentMethod.Club, ...clubPresets() });
  }

  function openPayModal(presets: IModalPayPresetProps = {}) {
    modals.show(
      ModalPay as any,
      {
        //order: order.value,
        type: PaymentType.Order,
        unpaidAmount: order.value.total - order.value.totalPaid,
        entityIds: [order.value.id],
        ...presets,
      },
      {
        name: 'order-pay',
        //classes: 'dialog--height-limit',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        clickToClose: false,
      },
      {
        paid: async (
          totalPaid: number,
          remainingToPay: number,
          sessionPaymentMethods: PaymentMethod[] = []
        ) => {
          // Orders outside the pending store set (unified history modal) get
          // no WS/store push after paying: refresh the viewed instance so
          // totals/paid state reflect immediately (Object.assign fallback).
          // Best-effort — a failed refresh must not block the auto-invoice.
          try {
            await ordersHelper.refreshOrder({ order: order.value });
          } catch (err) {
            console.error('Error refreshing order after payment', err);
          }

          if (
            restaurantStore.invoicingData?.data?.autoInvoice &&
            remainingToPay <= 0
          ) {
            const hasNonCash =
              sessionPaymentMethods.some(isAutoInvoicePaymentMethod) ||
              orderHasAutoInvoicePayments(order.value);

            if (hasNonCash) {
              nextTick(() => {
                createInvoice();
              });
            }
          }
        },
      }
    );
  }

  async function showAddDiscountModal() {
    let saving = false;

    modals.show(
      ModalOrderDiscount as any,
      {
        mode: 'create',
        defaultType: DiscountType.Manual,
        order: order.value,
      },
      {
        name: 'order-discount',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: 450,
      },
      {
        submit: async (data: any) => {
          if (saving) return;

          try {
            saving = true;
            const result = await discountsHelper.createDiscount(order, {
              type: data.type || DiscountType.Manual,
              isPercentage: data.isPercentage,
              value: data.value,
              metadata: data.metadata,
            });

            if (result) {
              modals.hide('order-discount');
            }
          } catch (e) {
            ui.serverError(e, 'Ocurrió un error al aplicar el descuento');
          } finally {
            saving = false;
          }
        },
      }
    );
  }

  async function showEditDiscountModal(discount: IDiscount) {
    let saving = false;

    modals.show(
      ModalOrderDiscount as any,
      {
        mode: 'edit',
        discount,
      },
      {
        name: 'order-discount',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: 450,
      },
      {
        submit: async (data: any) => {
          if (saving) return;

          try {
            saving = true;
            const result = await discountsHelper.updateDiscount(
              order,
              discount.id,
              {
                isPercentage: data.isPercentage,
                value: data.value,
                metadata: data.metadata,
              }
            );

            if (result) {
              modals.hide('order-discount');
            }
          } catch (e) {
            ui.serverError(e, 'Ocurrió un error al aplicar el descuento');
          } finally {
            saving = false;
          }
        },
      }
    );
  }

  async function confirmDeleteDiscount(discount: IDiscount) {
    // Deleting a club redemption credits the points back to the member
    // (server-side ledger reversal): the confirm copy must say so.
    const isClub = discount.type === DiscountType.Club;

    modals.show(
      ConfirmModal as any,
      {
        title: 'Eliminar descuento',
        message:
          `¿Está seguro que desea eliminar el descuento de ${discountsHelper.formatDiscountValue(
            discount,
            restaurantStore.currency.symbol
          )}?` +
          (isClub ? ' Los puntos canjeados se devolverán al socio.' : ''),
        acceptText: 'Eliminar',
        acceptColor: 'error',
      },
      {
        name: 'confirm-delete-discount',
        adaptive: true,
        height: 'auto',
        maxWidth: 400,
      },
      {
        confirm: async () => {
          try {
            await discountsHelper.deleteDiscount(order, discount.id);
            modals.hide('confirm-delete-discount');
          } catch (e) {
            ui.serverError(e, 'Ocurrió un error al eliminar el descuento');
          }
        },
      }
    );
  }

  // Points redemption is offered only when the active program redeems at
  // this branch and the order still has an unpaid amount to discount. The
  // per-member limits (balance, caps) are resolved inside the modal.
  const clubRedeemAvailable = computed(() => {
    const program = restaurantStore.restaurant?.club;
    if (
      !program?.pointsEnabled ||
      !(program.redeemPointsCost > 0) ||
      !(program.redeemValue > 0)
    ) {
      return false;
    }
    if (!clubProgramCoversBranch(program, restaurantStore.selectedBranch?.id)) {
      return false;
    }

    const o = order.value;
    if (!o) return false;
    return roundPrice((o.total ?? 0) - (o.totalPaid ?? 0)) > 0;
  });

  // Orders created without a member (POS V2 hosts): the panel's empty row /
  // strip "Socio" button link one now. Attaching alone earns the points.
  const clubAttachAvailable = computed(() => {
    const o = order.value;
    if (!o || !clubContext) return false;
    return isClubAttachAvailable({
      program: restaurantStore.restaurant?.club,
      branchId: restaurantStore.selectedBranch?.id,
      order: o,
    });
  });

  async function showAttachClubModal() {
    if (!clubContext) return;
    const result =
      await modals.showDynamicDialog<IClubOrderMemberContext | null>(
        V2ModalClubAttach,
        { orderId: order.value.id },
        'club-attach',
        { width: 480 }
      );
    if (!result?.member) return;

    // The refreshed order carries the member embed (the context resets on
    // that change); seeding the attach response then renders the panel
    // verified at once instead of after the debounced refetch. The member
    // is already linked server-side: a failed refresh must not hide that
    // (the WS order update brings the embed anyway).
    try {
      await ordersHelper.refreshOrder({ order: order.value });
    } catch (err) {
      console.error('Error refreshing order after club attach', err);
    }
    await nextTick();
    clubContext.apply(result);
    ui.showToast(
      AlertType.Success,
      String(i18n.t('posV2.clubAttachedToast', { name: result.member.name }))
    );
  }

  async function showRedeemPointsModal() {
    // Latest grant state right before opening (refresh never throws).
    await clubContext?.refresh();
    const result = await modals.showDynamicDialog<IClubRedeemResult | null>(
      V2ModalClubRedeem,
      {
        order: order.value,
        presetMember: clubContext?.member.value ?? null,
        presetVerified: clubContext?.verified.value ?? false,
      },
      'club-redeem',
      { width: 480 }
    );

    if (!result) return;

    await ordersHelper.refreshOrder({ order: order.value });
    ui.showToast(
      AlertType.Success,
      `Canje aplicado: -${money.formatComputedMoney(result.value)}`
    );
  }

  async function showTipModal() {
    modals.show(
      ModalPay as any,
      {
        type: PaymentType.Tip,
        unpaidAmount: 0, // Tips don't have a required amount
        entityIds: [order.value.id],
        title: 'Agregar propina',
        hiddenMethods: [
          PaymentMethod.CustomerAccount,
          PaymentMethod.Employee,
          PaymentMethod.Invitation,
          PaymentMethod.Club,
        ],
      },
      {
        name: 'tip-pay',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        clickToClose: false,
      },
      {
        paid: async () => {
          await ordersHelper.refreshOrder({ order: order.value });
          ui.showToast(AlertType.Success, 'Propina agregada correctamente');
        },
      }
    );
  }

  const invoiceLoading = ref(false);
  const invoiceInProgress = computed(() =>
    order.value ? invoiceAttempt.isInvoiceLive(order.value.id) : false
  );

  async function createInvoice() {
    const o = order.value;

    if (o.invoicingItemId || invoiceInProgress.value) return;
    let invoicingItem = null;

    // Invoice transparency: what will actually be invoiced, with the comps
    // (invitación/empleado) exclusion surfaced before emitting.
    const subtotal = roundPrice(Number(o.total ?? 0));
    const invoiceTotal = orderInvoiceableTotal(o);
    const summary = {
      ordersCount: 1,
      subtotal,
      excludedTotal: roundPrice(subtotal - invoiceTotal),
      invoiceTotal,
    };

    try {
      const invoiceData: {
        invoiceType: number;
        idType: number;
        id: string;
      } = await modals.showDynamicDialog(
        CreateInvoiceModalArg,
        { summary },
        'create-invoice'
      );

      if (!invoiceData) return;

      invoiceLoading.value = true;
      // Settles in the background if the user closes the modal; this
      // continuation still runs on terminal (toast + print below).
      invoicingItem = await invoiceAttempt.createInvoice(o.id, invoiceData);

      o.invoicingItemId = invoicingItem.id;
      ui.showToast(AlertType.Success, 'Factura emitida');
    } catch (e) {
      ui.serverError(e, 'Ocurrió un error al crear la factura');
    } finally {
      invoiceLoading.value = false;
    }

    if (
      o.invoicingItemId &&
      invoicingItem &&
      printing.isPrinterConnected(PrinterType.Ticket)
    ) {
      printing.printInvoice(invoicingItem).catch((e) => {
        ui.serverError(e, 'Ocurrió un error al imprimir la factura');
      });
    }
  }

  return {
    isPendingOrder,
    doPayment,
    payWithClubBalance,
    showAddDiscountModal,
    showEditDiscountModal,
    confirmDeleteDiscount,
    clubRedeemAvailable,
    showRedeemPointsModal,
    clubAttachAvailable,
    showAttachClubModal,
    showTipModal,
    createInvoice,
    invoiceLoading,
    invoiceInProgress,
  };
}
